import { HostBridge, hostBridge } from '@publicala/delfino';
import type { SecurityPreset } from '@publicala/delfino';
import type { VueEventBus, VuexStore } from './types';
import * as Sentry from '@sentry/browser';

// Commands configuration and registration
import { configurePlaybackCommands, registerPlaybackCommands } from './playback';
import { configureSessionCommands, registerSessionCommands } from './session';
import { configureNavigationCommands, registerNavigationCommands } from './navigation';

let _isInitialized = false;

/**
 * Determines the security preset based on the current environment.
 * @returns Security preset for the bridge configuration
 */
function getSecurityPreset(): SecurityPreset {
    return import.meta.env.VITE_ENV === 'production' ? 'production' : 'development';
}

/**
 * Configures the dependencies needed for host communication.
 * Must be called before initializeHostCommunication.
 * @param eventBus - Vue eventBus
 * @param store - Vuex store
 */
export function configureHostCommunication(eventBus: VueEventBus, store: VuexStore): void {
    configurePlaybackCommands(eventBus, store);
    configureSessionCommands(store);
    configureNavigationCommands(store);
}

/**
 * Initialize host communication via Delfino.
 * @returns Promise that resolves when hostBridge is ready
 */
export async function initializeHostCommunication(): Promise<boolean> {
    // Check if running standalone (not in iframe)
    if (HostBridge.isStandalone()) {
        return false;
    }

    try {
        hostBridge.configure({ security: getSecurityPreset() });

        registerPlaybackCommands();
        registerSessionCommands();
        registerNavigationCommands();

        await hostBridge.ready();
        _isInitialized = true;
        return true;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        Sentry.captureException(error, {
            tags: { component: 'hostCommunication' },
            extra: { context: '[Volpe] hostBridge initialization failed' },
        });

        console.warn(`[Volpe] hostBridge initialization failed, running in standalone mode: ${errorMessage}`);

        _isInitialized = false;
        return false;
    }
}

/**
 * Check if Delfino is initialized and connected.
 */
export function isHostCommunicationInitialized(): boolean {
    return _isInitialized;
}

export { HostBridge };
